/**
 * "Werkruimte herindelen": shows the plan (moves, renames, empty folders to the
 * trash, the new top level) and applies it only after confirmation.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FolderInput, Loader2, Pencil, Settings2, Trash2 } from 'lucide-react';
import { applyReorganization, planReorganization, type ReorgPlan } from '@nexus/workspace';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { Dialog } from './Dialog';

export function ReorganizeDialog() {
  const { t } = useTranslation();
  const open = useUi((s) => s.reorganizeOpen);
  const set = useUi((s) => s.set);
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => set({ reorganizeOpen: o })}
      title={t('reorganize.title')}
      wide
    >
      {open && <ReorganizeBody onClose={() => set({ reorganizeOpen: false })} />}
    </Dialog>
  );
}

function ReorganizeBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const store = useStore();
  const [plan, setPlan] = useState<ReorgPlan | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    let live = true;
    void planReorganization(store).then((p) => live && setPlan(p));
    return () => {
      live = false;
    };
  }, [store]);

  if (!plan)
    return (
      <p className="flex items-center gap-2 text-sm text-fg-muted">
        <Loader2 className="size-4 animate-spin" />
        {t('reorganize.planning')}
      </p>
    );
  if (plan.steps.length === 0)
    return <p className="text-sm text-fg-muted">{t('reorganize.nothing')}</p>;

  const apply = async () => {
    setProgress({ done: 0, total: 1 });
    await applyReorganization(store, plan, (done, total) => setProgress({ done, total }));
    onClose();
  };
  const of = (kind: string) => plan.steps.filter((s) => s.kind === kind);
  const section = 'mb-1 mt-3 flex items-center gap-1.5 text-xs font-medium text-fg-muted uppercase';

  return (
    <div className="flex flex-col text-sm" data-testid="reorganize-plan">
      <p className="text-fg-muted">{t('reorganize.intro')}</p>

      <h3 className={section}>{t('reorganize.top')}</h3>
      <ol className="list-decimal pl-6">
        {plan.root.map((r) => (
          <li key={r.id}>{r.title}</li>
        ))}
      </ol>

      {of('move').length > 0 && (
        <>
          <h3 className={section}>
            <FolderInput className="size-3.5" />
            {t('reorganize.moves')}
          </h3>
          <ul className="flex flex-col gap-0.5">
            {of('move').map((s) =>
              s.kind === 'move' ? (
                <li key={s.pageId} className="flex items-center gap-1.5">
                  {s.title} <ArrowRight className="size-3 text-fg-muted" />
                  {s.parentTitle ?? t('reorganize.topLevel')}
                </li>
              ) : null,
            )}
          </ul>
        </>
      )}

      {of('rename').length > 0 && (
        <>
          <h3 className={section}>
            <Pencil className="size-3.5" />
            {t('reorganize.renames')}
          </h3>
          <ul className="flex flex-col gap-0.5">
            {of('rename').map((s) =>
              s.kind === 'rename' ? (
                <li key={s.pageId} className="flex items-center gap-1.5">
                  {s.from} <ArrowRight className="size-3 text-fg-muted" /> {s.to}
                </li>
              ) : null,
            )}
          </ul>
        </>
      )}

      {of('trash').length > 0 && (
        <>
          <h3 className={section}>
            <Trash2 className="size-3.5" />
            {t('reorganize.trash')}
          </h3>
          <ul className="flex flex-col gap-0.5">
            {of('trash').map((s) =>
              s.kind === 'trash' ? <li key={s.pageId}>{s.title}</li> : null,
            )}
          </ul>
        </>
      )}

      {of('setting').length > 0 && (
        <>
          <h3 className={section}>
            <Settings2 className="size-3.5" />
            {t('reorganize.settings')}
          </h3>
          <ul className="flex flex-col gap-0.5">
            {of('setting').map((s) =>
              s.kind === 'setting' ? (
                <li key={s.key}>{t(`reorganize.setting.${s.key}`, { title: s.title })}</li>
              ) : null,
            )}
          </ul>
        </>
      )}

      <p className="mt-4 text-xs text-fg-muted">{t('reorganize.safety')}</p>
      <div className="mt-3 flex items-center justify-end gap-2">
        {progress && (
          <span className="mr-auto text-xs text-fg-muted" aria-live="polite">
            {t('reorganize.progress', progress)}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          disabled={!!progress}
          className="rounded-md px-3 py-1.5 text-fg-muted hover:bg-bg-hover"
        >
          {t('reorganize.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void apply()}
          disabled={!!progress}
          className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg disabled:opacity-50"
        >
          {t('reorganize.apply')}
        </button>
      </div>
    </div>
  );
}
